import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

// Update API_BASE to your correct backend URL
const API_BASE = 'https://personality-backend-production.up.railway.app';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  
  // Cache to prevent repeated API calls
  const profileCache = useRef(new Map());
  const lastFetchTime = useRef(new Map());
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Helper function to handle token expiration
  const handleTokenExpiration = useCallback(() => {
    console.log('🔄 Token expired, logging out user');
    // Clear localStorage
    localStorage.removeItem('personalityai_token');
    localStorage.removeItem('personalityai_user');
    
    // Clear state
    setUser(null);
    setUserProfile(null);
    setSession(null);
    
    // Clear cache
    profileCache.current.clear();
    lastFetchTime.current.clear();
    
    // Don't redirect automatically - let components handle this
    // window.location.href = '/login';
  }, []);

  // Helper function to refresh token
  const refreshToken = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('personalityai_token');
      if (!currentToken) return false;

      // Try to refresh the token by making a request to a simple endpoint
      const response = await fetch(`${API_BASE}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.token) {
          // Update stored token
          localStorage.setItem('personalityai_token', data.token);
          setSession({ access_token: data.token });
          console.log('✅ Token refreshed successfully');
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }, []);

  // Check if token is about to expire (within 5 minutes)
  const isTokenExpiringSoon = useCallback(() => {
    try {
      const token = localStorage.getItem('personalityai_token');
      if (!token) return true;

      // For JWT tokens, decode and check expiration
      if (token.includes('.')) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        
        return (expirationTime - currentTime) < fiveMinutes;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }, []);

  // Helper function to make authenticated API calls with token expiration handling
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    let token = localStorage.getItem('personalityai_token');
    
    if (!token) {
      handleTokenExpiration();
      return { success: false, error: 'No token available' };
    }

    // Check if token is expiring soon and try to refresh it
    if (isTokenExpiringSoon()) {
      console.log('🔄 Token expiring soon, attempting refresh...');
      const refreshed = await refreshToken();
      if (refreshed) {
        token = localStorage.getItem('personalityai_token'); // Get the new token
      }
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (response.status === 401) {
      try {
        const errorData = await response.json();
        if (errorData.token_expired) {
          // Try to refresh token one more time
          const refreshed = await refreshToken();
          if (refreshed) {
            // Retry the request with new token
            const newToken = localStorage.getItem('personalityai_token');
            const retryResponse = await fetch(url, {
              ...options,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${newToken}`,
                ...options.headers
              }
            });
            
            if (retryResponse.status === 401) {
              // Still getting 401, token refresh failed
              handleTokenExpiration();
              return { success: false, error: 'Token expired. Please log in again.' };
            }
            
            return retryResponse;
          } else {
            handleTokenExpiration();
            return { success: false, error: 'Token expired. Please log in again.' };
          }
        }
      } catch (e) {
        // If we can't parse the error response, assume token is invalid
        handleTokenExpiration();
        return { success: false, error: 'Authentication failed. Please log in again.' };
      }
    }

    return response;
  }, [handleTokenExpiration, isTokenExpiringSoon, refreshToken]);

  // Check for stored authentication on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for stored JWT token and user data
        const storedToken = localStorage.getItem('personalityai_token');
        const storedUser = localStorage.getItem('personalityai_user');
        
        if (storedToken && storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            setUserProfile(userData);
            setSession({ access_token: storedToken });
            console.log('✅ Restored user session from localStorage');
            
            // Load complete user profile including personality_factors
            setTimeout(() => {
              loadUserProfile();
            }, 100);
          } catch (error) {
            console.error('Error parsing stored user data:', error);
            // Clear invalid stored data
            localStorage.removeItem('personalityai_token');
            localStorage.removeItem('personalityai_user');
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up periodic token refresh check
  useEffect(() => {
    if (!user) return;

    const checkTokenExpiration = async () => {
      if (isTokenExpiringSoon()) {
        console.log('🔄 Token expiring soon, attempting automatic refresh...');
        const refreshed = await refreshToken();
        if (!refreshed) {
          console.log('❌ Automatic token refresh failed, logging out user');
          handleTokenExpiration();
        }
      }
    };

    // Check every 2 minutes
    const interval = setInterval(checkTokenExpiration, 2 * 60 * 1000);
    
    // Also check immediately
    checkTokenExpiration();

    return () => clearInterval(interval);
  }, [user, isTokenExpiringSoon, refreshToken, handleTokenExpiration]);

  const signUp = async (email, password, fullName) => {
    try {
      setLoading(true);
      
      console.log('Attempting registration with backend...');
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name: fullName,
          full_name: fullName
        })
      });

      const data = await response.json();
      console.log('Registration response:', data);

      if (data.success) {
        // Store auth data
        localStorage.setItem('personalityai_token', data.token);
        localStorage.setItem('personalityai_user', JSON.stringify(data.user));
        
        setUser(data.user);
        setUserProfile(data.user);
        setSession({ access_token: data.token });
        
        toast.success('Account created successfully! Welcome to PersonalityAI! 🎉');
        return { success: true };
      } else {
        toast.error(data.error || 'Registration failed');
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Network error during registration');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      
      console.log('Attempting login with backend...');
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await response.json();
      console.log('Login response:', data);

      if (data.success) {
        // Store auth data in localStorage
        localStorage.setItem('personalityai_token', data.token);
        localStorage.setItem('personalityai_user', JSON.stringify(data.user));
        
        // Update state
        setUser(data.user);
        setUserProfile(data.user);
        setSession({ access_token: data.token });
        
        toast.success(`Welcome back${data.user.subscription_tier === 'pro' ? ', Pro user' : ''}! 🚀`);
        return { success: true, user: data.user };
      } else {
        toast.error(data.error || 'Login failed');
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Network error during login');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signInWithProvider = async (provider) => {
    try {
      setLoading(true);
      
      if (provider === 'google') {
        // Use our custom Google OAuth implementation
        return await signInWithGoogle();
      } else {
        // For other providers, use Supabase OAuth flow
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: provider,
          options: {
            redirectTo: `${window.location.origin}/auth/callback`
          }
        });

        if (error) {
          toast.error(error.message);
          return { success: false, error: error.message };
        }

        return { success: true };
      }
    } catch (error) {
      toast.error('Social login failed');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Check if Google Identity Services is available
      if (!window.google || !window.google.accounts) {
        toast.error('Google OAuth not available. Please refresh the page.');
        return { success: false, error: 'Google OAuth not available' };
      }

      // Use the modern Google Identity Services
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '177169302683-nturpvck4dgjdob9f2cbsupg6qasqt2b.apps.googleusercontent.com',
        scope: 'openid email profile',
        callback: async (response) => {
          if (response.error) {
            toast.error('Google login failed: ' + response.error);
            return { success: false, error: response.error };
          }

          try {
            // Get user info using the access token
            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
              headers: {
                'Authorization': `Bearer ${response.access_token}`
              }
            });

            if (!userInfoResponse.ok) {
              throw new Error('Failed to get user info from Google');
            }

            const userInfo = await userInfoResponse.json();

            // Send user info to our backend
            const backendResponse = await fetch(`${API_BASE}/auth/google`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ 
                accessToken: response.access_token,
                userInfo: userInfo
              })
            });

            const data = await backendResponse.json();

            if (data.success) {
              // Store auth data
              localStorage.setItem('personalityai_token', data.token);
              localStorage.setItem('personalityai_user', JSON.stringify(data.user));
              
              setUser(data.user);
              setUserProfile(data.user);
              setSession({ access_token: data.token });
              
              toast.success(`Welcome back${data.user.subscription_tier === 'pro' ? ', Pro user' : ''}! 🚀`);
              return { success: true, user: data.user };
            } else {
              toast.error(data.error || 'Google login failed');
              return { success: false, error: data.error };
            }
          } catch (error) {
            console.error('Error processing Google login:', error);
            toast.error('Google login failed');
            return { success: false, error: error.message };
          }
        }
      });

      // Request the token
      client.requestAccessToken();

    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google login failed');
      return { success: false, error: error.message };
    }
  };

  const loadGoogleOAuthScript = () => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.accounts) {
        resolve();
        return;
      }

      // Load the modern Google Identity Services script
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait a bit for the script to initialize
        setTimeout(() => {
          if (window.google && window.google.accounts) {
            resolve();
          } else {
            reject(new Error('Google Identity Services failed to load'));
          }
        }, 100);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const loadUserProfile = async () => {
    try {
      // Check cache first
      const cacheKey = 'userProfile';
      const now = Date.now();
      const lastFetch = lastFetchTime.current.get(cacheKey);
      
      if (lastFetch && (now - lastFetch) < CACHE_DURATION) {
        const cached = profileCache.current.get(cacheKey);
        if (cached) {
          setUserProfile(cached);
          return cached;
        }
      }

      // Fetch from backend using authenticated request helper
      const response = await makeAuthenticatedRequest(`${API_BASE}/api/user/profile`);
      
      if (response.success === false) {
        // Token expired or other auth error handled by helper
        return null;
      }

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.user) {
          // Cache the result
          profileCache.current.set(cacheKey, data.user);
          lastFetchTime.current.set(cacheKey, now);
          
          setUserProfile(data.user);
          return data.user;
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const signOut = async () => {
    try {
      // Clear localStorage
      localStorage.removeItem('personalityai_token');
      localStorage.removeItem('personalityai_user');
      
      // Clear state
      setUser(null);
      setUserProfile(null);
      setSession(null);
      
      // Clear cache
      profileCache.current.clear();
      lastFetchTime.current.clear();
      
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const savePersonalityProfile = async (personalityData) => {
    try {
      if (!user) return { success: false, error: 'No user logged in' };

      const response = await makeAuthenticatedRequest(`${API_BASE}/api/personality`, {
        method: 'POST',
        body: JSON.stringify({
          ...personalityData,
          user_id: user.id
        })
      });

      if (response.success === false) {
        return response; // Token expired or other auth error handled by helper
      }

      const data = await response.json();

      if (data.success) {
        toast.success('Personality profile saved successfully');
        return { success: true };
      } else {
        toast.error('Failed to save personality profile');
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Save personality error:', error);
      toast.error('Save failed');
      return { success: false, error: error.message };
    }
  };

  const enhancePrompt = async (prompt, platform = 'web') => {
    try {
      if (!user) {
        return {
          success: false,
          error: 'Please log in first'
        };
      }

      const response = await makeAuthenticatedRequest(`${API_BASE}/api/ai/inject-personality`, {
        method: 'POST',
        body: JSON.stringify({ prompt })
      });

      if (response.success === false) {
        return response; // Token expired or other auth error handled by helper
      }

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          original: data.original_prompt,
          enhanced: data.enhanced_prompt,
          personality_context: data.personality_context
        };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error enhancing prompt:', error);
      return { success: false, error: error.message };
    }
  };

  const createCheckoutSession = async (plan = 'pro') => {
    try {
      if (!user) {
        return { success: false, error: 'Please log in first' };
      }

      const response = await makeAuthenticatedRequest(`${API_BASE}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Origin': window.location.origin
        },
        body: JSON.stringify({ plan })
      });

      if (response.success === false) {
        return response; // Token expired or other auth error handled by helper
      }

      const data = await response.json();

      if (data.success) {
        return { success: true, checkout_url: data.checkout_url };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Checkout session error:', error);
      return { success: false, error: error.message };
    }
  };

  const getSubscriptionStatus = async () => {
    try {
      if (!user) return { success: false, error: 'No user logged in' };

      const response = await makeAuthenticatedRequest(`${API_BASE}/api/stripe/subscription-status`);

      if (response.success === false) {
        return response; // Token expired or other auth error handled by helper
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Subscription status error:', error);
      return { success: false, error: error.message };
    }
  };

  const getEarnings = async () => {
    try {
      if (!user) return { success: false, error: 'No user logged in' };

      const response = await makeAuthenticatedRequest(`${API_BASE}/api/analytics/earnings`);

      if (response.success === false) {
        return response; // Token expired or other auth error handled by helper
      }

      const data = await response.json();
      
      if (data.success) {
        // Convert analytics data to earnings format
        return {
          success: true,
          data: [
            {
              month: new Date().getMonth() + 1,
              year: new Date().getFullYear(),
              total_earned: data.total_earnings || 0
            }
          ]
        };
      }
      
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Earnings error:', error);
      return { success: false, error: error.message };
    }
  };

  const getInteractions = async (limit = 50) => {
    try {
      if (!user) return {success: false, error: 'No user logged in' };

      const response = await makeAuthenticatedRequest(`${API_BASE}/api/analytics/usage`);

      if (response.success === false) {
        return response; // Token expired or other auth error handled by helper
      }

      const data = await response.json();
      
      if (data.success) {
        // Convert analytics data to interactions format
        const interactions = data.interactions || data.usage_data || [];
        return { success: true, data: interactions };
      }
      
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Interactions error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    userProfile,
    session,
    loading,
    signUp,
    signIn,
    signInWithProvider,
    signInWithGoogle,
    signOut,
    loadUserProfile,
    savePersonalityProfile,
    enhancePrompt,
    createCheckoutSession,
    getSubscriptionStatus,
    getEarnings,
    getInteractions,
    isAuthenticated: !!user,
    makeAuthenticatedRequest,
    refreshToken,
    isTokenExpiringSoon,
    handleTokenExpiration,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
